// Données T2/Production (liveQuery) : file active triée par heure + lignes +
// habitude client + compte de train, PLUS (LOT 7 §1) la zone À PRÉPARER
// (commandee — toute commande validée y entre automatiquement) et les Prêtes.
// 100 % via wrappers (getCommandesEnFile, getLignesCommandes, withTenant pour le
// catalogue clients) — jamais de table brute en composant (INV-C).
// N'expose JAMAIS le téléphone (RGPD §8) : seuls nom snapshot et habitude sortent.
import { useLiveQuery } from 'dexie-react-hooks';
import { getDb } from '@/db';
import { withTenant } from '@/db/tenant';
import { getCommandesEnFile, getLignesCommandes } from '@/db/transactions';
import {
  versChargeCommandes,
  type ChargeCommande,
} from '@/lib/heure-capacite';
import type { Commande, CommandePizza, StatutCommande } from '@/db/schema';

// Commandes « en cours » à l'écran Production : ni 'commandee' (zone À PRÉPARER),
// ni 'prete' (zone Prêtes) ni terminales. Tri par heure (getCommandesEnFile déjà trié ASC).
const STATUTS_ACTIFS_T2: readonly StatutCommande[] = [
  'priorisee',
  'en_preparation',
  'enfournee',
  'en_finalisation',
];

export interface TuileT2 {
  commande: Commande;
  lignes: CommandePizza[];
  habitude: string | null;
  trainN: number;
}

export interface T2FileData {
  aPreparer: TuileT2[];
  enCours: TuileT2 | null;
  suivantes: TuileT2[];
  reste: number;
  pretes: TuileT2[];
  heureTete: number | null;
  /** Vue moteur de TOUTE la file ouverte (délais §2 — y compris au-delà du top 3). */
  chargeCommandes: ChargeCommande[];
}

const EMPTY: T2FileData = {
  aPreparer: [],
  enCours: null,
  suivantes: [],
  reste: 0,
  pretes: [],
  heureTete: null,
  chargeCommandes: [],
};

export function useT2File(): T2FileData {
  const data = useLiveQuery(
    async () => {
      const ouvertes = await getCommandesEnFile();
      const aPreparerC = ouvertes.filter((c) => c.statut === 'commandee');
      const actives = ouvertes.filter((c) =>
        STATUTS_ACTIFS_T2.includes(c.statut),
      );
      const pretesC = ouvertes.filter((c) => c.statut === 'prete');
      // Tri Prêtes (micro-retouche 7g, T2 UNIQUEMENT) : par INSTANT DE PASSAGE
      // EN PRÊTE, la plus ancienne EN TÊTE (elle refroidit — à remettre
      // d'abord). CODE-REALITY : updatedAt est écrasé à chaque transition par
      // l'écrivain unique updateCommandeStatut ({statut, updatedAt}) — pour
      // une prete, il vaut précisément l'instant du passage en prete.
      // L'échéance ne commande plus cette zone. Tie-break id. Zéro schéma.
      pretesC.sort(
        (a, b) => a.updatedAt - b.updatedAt || (a.id ?? 0) - (b.id ?? 0),
      );

      const concernees = [...aPreparerC, ...actives, ...pretesC];
      const lignes = await getLignesCommandes(
        concernees.map((c) => c.id as number),
      );
      const lignesParCommande = new Map<number, CommandePizza[]>();
      for (const l of lignes) {
        const arr = lignesParCommande.get(l.commandeId) ?? [];
        arr.push(l);
        lignesParCommande.set(l.commandeId, arr);
      }

      // Compte de train sur toute la file ouverte (le train peut inclure des prêtes).
      const parTrain = new Map<number, number>();
      for (const c of ouvertes) {
        if (c.train_id !== undefined) {
          parTrain.set(c.train_id, (parTrain.get(c.train_id) ?? 0) + 1);
        }
      }

      // Habitudes des clients concernés (via wrapper de scope, jamais le téléphone).
      const clientIds = [
        ...new Set(
          concernees
            .map((c) => c.clientId)
            .filter((x): x is number => typeof x === 'number'),
        ),
      ];
      const habitudes = new Map<number, string>();
      for (const cid of clientIds) {
        const client = await withTenant(getDb().clients).get(cid);
        if (client?.habitude) habitudes.set(cid, client.habitude);
      }

      const toTuile = (c: Commande): TuileT2 => ({
        commande: c,
        lignes: lignesParCommande.get(c.id as number) ?? [],
        habitude:
          c.clientId !== undefined ? (habitudes.get(c.clientId) ?? null) : null,
        trainN:
          c.train_id !== undefined ? (parTrain.get(c.train_id) ?? 0) : 0,
      });

      const tuiles = actives.map(toTuile);
      return {
        aPreparer: aPreparerC.map(toTuile),
        enCours: tuiles[0] ?? null,
        suivantes: tuiles.slice(1, 3),
        reste: Math.max(0, tuiles.length - 3),
        pretes: pretesC.map(toTuile),
        heureTete: tuiles[0]?.commande.heure_retrait_proposee ?? null,
        chargeCommandes: versChargeCommandes(concernees, lignesParCommande),
      };
    },
    [],
    EMPTY,
  );
  return data;
}
